import VueApollo from 'vue-apollo'
import { ApolloClient } from 'apollo-boost'
import { ApolloLink, from, split } from 'apollo-link'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { onError } from 'apollo-link-error'
import { RetryLink } from 'apollo-link-retry'
import { HttpLink } from 'apollo-link-http'
import { WebSocketLink } from 'apollo-link-ws'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import fetch from 'cross-fetch'

import { boot } from 'quasar/wrappers'
import { getMainDefinition } from 'apollo-utilities'

import {FUSIONAUTH_USER, TOKEN_NAME, TOKEN_REFRESH_NAME} from 'src/lib/constants'
import {fusionClient} from "boot/fusionAuth";

const ENV = process.env.DEV ? process.env.NODE_ENV : process.env.ENVIRONMENT
const GRAPHQL_URI = require('../lib/deployment.json')['' + ENV].GRAPHQL_URL
const HTTP_GRAPHQL_URI = (process.env.PROD ? 'https://' : 'http://') + GRAPHQL_URI
const WS_GRAPHQL_URI = (process.env.PROD ? 'wss://' : 'ws://') + GRAPHQL_URI
declare const window

function getAuth (tokenName) {
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem(tokenName)
    return token ? `Bearer ${token}` : null
  }
}

const retryMiddleWare = new RetryLink()

const authMiddleWare = new ApolloLink((operation, forward) => {
  const otherHeaders = operation.getContext().headers
  if (getAuth(TOKEN_NAME)) {
    operation.setContext({
      headers: {
        ...otherHeaders,
        authorization: getAuth(TOKEN_NAME)
      }
    })
  } else {
    operation.setContext({
      headers: {
        ...otherHeaders
      }
    })
  }
  return forward(operation)
})

const httpLink = new HttpLink({
  uri: HTTP_GRAPHQL_URI,
  // @ts-ignore
  fetch
})

async function validateToken(token: string): Promise<{
  success: boolean;
  expired: boolean;
  data: {token: string};
  error?: {
    message: string;
    details: any;
  };
}> {
  let result
  try {
    result = await fusionClient.validateJWT(token)
  } catch (e) {
    // catching the error only returns status codes and no additional information.
    result = e
    if (process.env.DEV) console.error(e)
  }

  if (result.statusCode === 200) {
    return {
      success: true,
      expired: false,
      data: {token: token}
    }
  } else if (result.statusCode === 401) {
    return {
      success: false,
      expired: true,
      data: {token: ''},
      // added a custom error object with a message and status code to signify that the token is expired
      error: {message: 'The token is expired', details: result}
    }
  } else {
    return {
      success: false,
      expired: false,
      data: {token: ''},
      // added a custom error message with message and other error details to signify that there is an internal error.
      error: {message: 'The token is expired', details: result}
    }
  }

}

async function exchangeRefreshToken(refreshToken: string, token: string): Promise<{
  success: boolean;
  data: {token: string, refreshToken: string};
  error?: {
    message: string;
    details: any;
  }
}> {
  let result;
  try {
    result = await fusionClient.exchangeRefreshTokenForJWT({
      refreshToken,
      token
    })
  } catch (e) {
    result = e
    if (process.env.DEV) console.error(e)
  }
  if (result.statusCode === 200) {
    return {
      success: true,
      data: {token: result.response.token, refreshToken: result.response.refreshToken}
    }
  } else {
    return {
      success: false,
      data: {token: '', refreshToken: ''},
      error: {message: 'Unable to retrieve a new token', details: result}
    }
  }
}

function redirectToLogin(message: string) {
  alert(message);
  window.location.href = '/#/login';
}

function handleSessionExpiration(){
  localStorage.removeItem(TOKEN_NAME)
  localStorage.removeItem(TOKEN_REFRESH_NAME)
  // removing also the user's stored JSON information to ensure a clean re-login.
  localStorage.removeItem(FUSIONAUTH_USER)
  // By closing the WebSocket connection, it ensures that no further network errors occur due to invalid or expired tokens in subscription queries.
  // forcing it to close and no attempts in reconnecting.
  wsClient.close(true, true)
}

async function getConnectionParams() {
  const token = window.localStorage.getItem(TOKEN_NAME);
  const refreshToken = window.localStorage.getItem(TOKEN_REFRESH_NAME);

  if (!token || !refreshToken) {
    handleSessionExpiration()
    redirectToLogin('Your authentication token is missing. You will be redirected to the login page')
    return {}
  }

  let isValidToken = await validateToken(token)

  if(isValidToken.success) {
    return {Authorization: `Bearer ${isValidToken.data.token}`, headers: {Authorization: `Bearer ${isValidToken.data.token}`}}
  }

  if(!isValidToken.success && isValidToken.expired) {
    let tokenExchangeResult = await exchangeRefreshToken(refreshToken, token)

    if(tokenExchangeResult.success){
      let newToken = tokenExchangeResult.data.token
      let newRefreshToken = tokenExchangeResult.data.refreshToken

      localStorage.setItem(TOKEN_NAME, newToken)
      localStorage.setItem(TOKEN_REFRESH_NAME, newRefreshToken)
      return {Authorization: `Bearer ${newToken}`, headers: {Authorization: `Bearer ${newToken}`}}
    } else {
      if (process.env.DEV) console.error(tokenExchangeResult.error)
      handleSessionExpiration()
      redirectToLogin("Your session has expired, you will be redirected to login page.")
      return {}
    }
  }

  if(!isValidToken.success && !isValidToken.expired) {
    if (process.env.DEV) console.error(isValidToken.error)
    handleSessionExpiration()
    redirectToLogin("An error occurred. Please try again later.")
    return {}
  }

}

const wsClient = new SubscriptionClient(WS_GRAPHQL_URI, {
  reconnect: true,
  lazy: true,
  connectionParams: getConnectionParams
});

const wsLink = new WebSocketLink(wsClient)

const connectionLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
  },
  wsLink,
  httpLink
)

const errorMiddleWare = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    // for (const err of graphQLErrors) {
    graphQLErrors.map(({ message, locations, path, extensions }) => {
      const oldHeaders = operation.getContext().headers
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
      switch (extensions.code) {
        case 'UNAUTHENTICATED':
          // error code is set to UNAUTHENTICATED
          // when AuthenticationError thrown in resolver

          // modify the operation context with a new token
          operation.setContext({
            headers: {
              ...oldHeaders,
              authorization: getAuth(TOKEN_NAME)
            }
          })
          // retry the request, returning the new observable
          return forward(operation)
      }
    })
  }
  if (networkError) console.log('[Network error]:', networkError)
})

const link = from([
  retryMiddleWare,
  errorMiddleWare,
  authMiddleWare.concat(connectionLink)
])
const cache = new InMemoryCache()

const apolloClient = new ApolloClient({
  link,
  cache,
  name: 'able-web-client',
  version: '1.0',
  queryDeduplication: false,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network'
    },
    query: {
      fetchPolicy: 'cache-first'
    }
  },
  connectToDevTools: true
})

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient
})

export default boot(({ app, Vue }: any) => {
  Vue.use(VueApollo)
  app.apolloProvider = apolloProvider
})

export function closeApolloWebSocket () {
  console.debug('Closing apollo web socket!')
  wsClient.unsubscribeAll()
  wsClient.close(true)
}
